(function initializeScanningPricingCalculator(global) {
  'use strict';

  if (!global) return;
  if (global.StackhouseScanningCalculator) {
    if (global.document) global.StackhouseScanningCalculator.initAll(global.document);
    return;
  }

  var AREA_EPSILON = 0.000001;
  var ADDITIONAL_ARTWORK_DISCOUNT_PERCENT = 20;

  function finiteNumber(value) {
    var parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function normalizeQuantity(value) {
    var parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  function normalizeUnits(value) {
    return value === 'cm' ? 'cm' : 'in';
  }

  function inchesFrom(value, units) {
    return units === 'cm' ? value / 2.54 : value;
  }

  function findTier(tiers, areaSquareInches) {
    return tiers.find(function findMatchingTier(tier) {
      return areaSquareInches <= Number(tier.maxArea) + AREA_EPSILON;
    }) || null;
  }

  function calculateEstimate(options) {
    var width = finiteNumber(options.width);
    var height = finiteNumber(options.height);
    var units = normalizeUnits(options.units);
    var quantity = normalizeQuantity(options.quantity);
    var rushPercent = finiteNumber(options.rushPercent) || 0;
    var maxStandardSide = finiteNumber(options.maxStandardSide) || 60;
    var tiers = Array.isArray(options.tiers) ? options.tiers : [];

    if (!(width > 0) || !(height > 0)) {
      return {
        status: 'empty',
        width: width,
        height: height,
        units: units,
        quantity: quantity,
        rush: Boolean(options.rush),
        rushPercent: rushPercent
      };
    }

    var widthInches = inchesFrom(width, units);
    var heightInches = inchesFrom(height, units);
    var areaSquareInches = widthInches * heightInches;
    var longSideInches = Math.max(widthInches, heightInches);
    var tier = findTier(tiers, areaSquareInches);

    if (longSideInches > maxStandardSide + AREA_EPSILON || !tier) {
      return {
        status: 'custom',
        width: width,
        height: height,
        widthInches: widthInches,
        heightInches: heightInches,
        areaSquareInches: areaSquareInches,
        units: units,
        quantity: quantity,
        rush: Boolean(options.rush),
        rushPercent: rushPercent,
        maxStandardSide: maxStandardSide
      };
    }

    var pricePerScan = Number(tier.price);
    var discount = pricePerScan * Math.max(0, quantity - 1) * (ADDITIONAL_ARTWORK_DISCOUNT_PERCENT / 100);
    var subtotal = pricePerScan * quantity - discount;
    var rushFee = options.rush ? subtotal * (rushPercent / 100) : 0;

    return {
      status: 'valid',
      width: width,
      height: height,
      widthInches: widthInches,
      heightInches: heightInches,
      areaSquareInches: areaSquareInches,
      units: units,
      quantity: quantity,
      rush: Boolean(options.rush),
      rushPercent: rushPercent,
      tier: tier,
      pricePerScan: pricePerScan,
      discount: discount,
      rushFee: rushFee,
      total: subtotal + rushFee,
      maxStandardSide: maxStandardSide
    };
  }

  function formatMoney(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  }

  function formatArea(value) {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: value < 100 ? 1 : 0
    }).format(value) + ' sq in';
  }

  function displayTierLabel(label) {
    return String(label || '').replace(/(\d)x(\d)/g, '$1 × $2');
  }

  function unitLabel(units) {
    return units === 'cm' ? 'cm' : 'in';
  }

  function buildInquiryNotes(state) {
    if (!state || state.status === 'empty') return '';

    var lines = [
      state.status === 'valid'
        ? 'Artwork scanning estimate from thestackhouse.com'
        : 'Artwork scanning custom quote request from thestackhouse.com',
      'Dimensions: ' + state.width + ' × ' + state.height + ' ' + unitLabel(state.units),
      'Quantity: ' + state.quantity,
      'Rush: ' + (state.rush ? 'Yes (' + state.rushPercent + '%)' : 'No'),
      'Area: ' + formatArea(state.areaSquareInches)
    ];

    if (state.status === 'valid') {
      lines.push('Pricing tier: ' + displayTierLabel(state.tier.label));
      if (state.discount > 0) lines.push('Additional artwork discount (20%): -' + formatMoney(state.discount));
      lines.push('Estimated total: ' + formatMoney(state.total));
      lines.push('Final pricing confirmed at intake.');
    } else {
      lines.push('Outside the standard ' + state.maxStandardSide + ' × ' + state.maxStandardSide + ' in pricing range.');
      lines.push('Please review this piece for a custom quote.');
    }

    return lines.join('\n');
  }

  function buildInquiryUrl(baseUrl, state) {
    var url = new URL(baseUrl);
    url.searchParams.set('howMany', String(state.quantity || 1));

    var notes = buildInquiryNotes(state);
    if (notes) url.searchParams.set('additionalNotes', notes);

    // Jotform treats `+` literally in long-text URL prefills, so preserve
    // URLSearchParams encoding while using `%20` for spaces.
    return url.toString().replace(/\+/g, '%20');
  }

  function parseTiers(root) {
    var source = root.querySelector('[data-scan-tiers]');
    if (!source) return [];

    try {
      return JSON.parse(source.textContent).map(function normalizeTier(tier) {
        return {
          sku: String(tier.sku || ''),
          label: String(tier.label || ''),
          maxArea: Number(tier.maxArea),
          price: Number(tier.price)
        };
      }).filter(function validTier(tier) {
        return tier.label && Number.isFinite(tier.maxArea) && Number.isFinite(tier.price);
      }).sort(function sortByArea(left, right) {
        return left.maxArea - right.maxArea;
      });
    } catch (error) {
      return [];
    }
  }

  function copyText(value) {
    if (global.navigator && global.navigator.clipboard && global.navigator.clipboard.writeText) {
      return global.navigator.clipboard.writeText(value);
    }

    return new Promise(function fallbackCopy(resolve, reject) {
      if (!global.document || !global.document.body) {
        reject(new Error('Clipboard unavailable'));
        return;
      }

      var field = global.document.createElement('textarea');
      field.value = value;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      global.document.body.appendChild(field);
      field.select();

      try {
        if (global.document.execCommand('copy')) resolve();
        else reject(new Error('Copy command failed'));
      } catch (error) {
        reject(error);
      } finally {
        field.remove();
      }
    });
  }

  function initialize(root) {
    if (!root || root.dataset.scanningCalculatorInitialized === 'true') return;

    var form = root.querySelector('[data-scan-form]');
    var inputs = {
      width: root.querySelector('[data-scan-width]'),
      height: root.querySelector('[data-scan-height]'),
      units: root.querySelector('[data-scan-units]'),
      quantity: root.querySelector('[data-scan-quantity]'),
      rush: root.querySelector('[data-scan-rush]')
    };
    var output = {
      area: root.querySelector('[data-scan-area]'),
      tier: root.querySelector('[data-scan-tier]'),
      price: root.querySelector('[data-scan-price]'),
      quantity: root.querySelector('[data-scan-quantity-output]'),
      discountRow: root.querySelector('[data-scan-discount-row]'),
      discount: root.querySelector('[data-scan-discount-output]'),
      rushRow: root.querySelector('[data-scan-rush-row]'),
      rush: root.querySelector('[data-scan-rush-output]'),
      total: root.querySelector('[data-scan-total]'),
      notice: root.querySelector('[data-scan-notice]'),
      maxLabel: root.querySelector('[data-scan-max-label]'),
      rushPercent: root.querySelector('[data-scan-rush-percent]')
    };
    var inquiryLink = root.querySelector('[data-scan-inquiry-link]');
    var copyButton = root.querySelector('[data-scan-copy]');
    var copyStatus = root.querySelector('[data-scan-copy-status]');
    var tiers = parseTiers(root);
    var rushPercent = finiteNumber(root.dataset.rushPercent) || 0;
    var maxStandardSide = finiteNumber(root.dataset.maxStandardSide) || 60;
    var inquiryUrl = root.dataset.inquiryUrl;
    var currentState = null;

    if (!form || !inputs.width || !inputs.height || !inputs.units || !inputs.quantity || !inputs.rush || !inquiryLink) return;

    root.dataset.scanningCalculatorInitialized = 'true';
    inputs.units.value = normalizeUnits(root.dataset.defaultUnits);
    output.rushPercent.textContent = rushPercent + '%';

    function applyUnitLimit() {
      var units = normalizeUnits(inputs.units.value);
      var max = units === 'cm' ? maxStandardSide * 2.54 : maxStandardSide;
      var maxText = units === 'cm' ? max.toFixed(1) + ' cm' : max + ' inches';
      inputs.width.max = String(units === 'cm' ? max.toFixed(1) : max);
      inputs.height.max = String(units === 'cm' ? max.toFixed(1) : max);
      output.maxLabel.textContent = maxText;
    }

    function setNotice(message) {
      output.notice.textContent = message || '';
      output.notice.hidden = !message;
    }

    function render() {
      currentState = calculateEstimate({
        width: inputs.width.value,
        height: inputs.height.value,
        units: inputs.units.value,
        quantity: inputs.quantity.value,
        rush: inputs.rush.checked,
        rushPercent: rushPercent,
        maxStandardSide: maxStandardSide,
        tiers: tiers
      });

      output.quantity.textContent = String(currentState.quantity);
      copyStatus.textContent = '';

      if (currentState.status === 'empty') {
        output.area.textContent = '0 sq in';
        output.tier.textContent = 'Not calculated';
        output.price.textContent = formatMoney(0);
        output.discountRow.hidden = true;
        output.discount.textContent = formatMoney(0);
        output.rushRow.hidden = true;
        output.rush.textContent = formatMoney(0);
        output.total.textContent = formatMoney(0);
        setNotice('');
        inquiryLink.textContent = 'Start a Scanning Inquiry';
        inquiryLink.href = inquiryUrl;
        copyButton.disabled = true;
        delete root.dataset.estimate;
        delete root.dataset.tier;
        return;
      }

      output.area.textContent = formatArea(currentState.areaSquareInches);
      inquiryLink.href = buildInquiryUrl(inquiryUrl, currentState);
      copyButton.disabled = false;

      if (currentState.status === 'custom') {
        output.tier.textContent = 'Custom quote';
        output.price.textContent = 'N/A';
        output.discountRow.hidden = true;
        output.discount.textContent = formatMoney(0);
        output.rushRow.hidden = true;
        output.rush.textContent = formatMoney(0);
        output.total.textContent = 'Contact us';
        setNotice('This piece is outside the standard pricing range. We can still review it for a custom quote.');
        inquiryLink.textContent = 'Request a Custom Quote';
        root.dataset.tier = 'custom_quote';
        delete root.dataset.estimate;
        return;
      }

      output.tier.textContent = displayTierLabel(currentState.tier.label);
      output.price.textContent = formatMoney(currentState.pricePerScan);
      output.discountRow.hidden = currentState.discount === 0;
      output.discount.textContent = '-' + formatMoney(currentState.discount);
      output.rushRow.hidden = !currentState.rush;
      output.rush.textContent = formatMoney(currentState.rushFee);
      output.total.textContent = formatMoney(currentState.total);
      setNotice('');
      inquiryLink.textContent = 'Start a Scanning Inquiry';
      root.dataset.estimate = currentState.total.toFixed(2);
      root.dataset.tier = currentState.tier.label;
    }

    inputs.units.addEventListener('change', function handleUnitChange() {
      applyUnitLimit();
      render();
    });
    form.addEventListener('input', render);
    form.addEventListener('change', render);

    copyButton.addEventListener('click', function handleCopy() {
      if (!currentState || currentState.status === 'empty') return;

      copyText(buildInquiryNotes(currentState)).then(function copied() {
        copyStatus.textContent = 'Estimate copied.';
      }).catch(function copyFailed() {
        copyStatus.textContent = 'Could not copy the estimate. Please use the inquiry button.';
      });
    });

    applyUnitLimit();
    render();
  }

  function initAll(scope) {
    if (!scope || !scope.querySelectorAll) return;
    if (scope.matches && scope.matches('[data-scanning-calculator]')) initialize(scope);
    scope.querySelectorAll('[data-scanning-calculator]').forEach(initialize);
  }

  var api = {
    buildInquiryNotes: buildInquiryNotes,
    buildInquiryUrl: buildInquiryUrl,
    calculateEstimate: calculateEstimate,
    displayTierLabel: displayTierLabel,
    findTier: findTier,
    formatArea: formatArea,
    initAll: initAll
  };

  global.StackhouseScanningCalculator = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;

  if (global.document) {
    if (global.document.readyState === 'loading') {
      global.document.addEventListener('DOMContentLoaded', function onReady() {
        initAll(global.document);
      }, { once: true });
    } else {
      initAll(global.document);
    }

    global.document.addEventListener('shopify:section:load', function onSectionLoad(event) {
      initAll(event.target);
    });
  }
})(typeof window !== 'undefined' ? window : globalThis);
